/**
 * Arena kit for the Writing Town engines (sound / sentence / story games).
 * Everything here renders inside <LearnStage tone="light">: ink text on paper,
 * wooden sticker tiles sized with var(--tile), boards that size to their
 * content and sit centred in the arena, SVG icons only (no emoji).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN, stickerButton } from "../../theme";
import { writingAccentWash } from "../writingAssets";
import {
  CastPicture,
  CounterPill,
  PICTURE_FRAME,
  PanelHeader,
  WordPicture,
  paperSurface,
  tileEdge,
} from "./shared";

const INK = LEARN.color.ink;

// ── colour helpers ───────────────────────────────────────────────────────────

export function darken(hex, amount = 0.32) {
  const raw = String(hex || LEARN.color.lavender).replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return LEARN.color.lavenderDeep;
  const ch = (v) => Math.max(0, Math.round(v * (1 - amount))).toString(16).padStart(2, "0");
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

export const wash = (accent, alpha = 0.1) => writingAccentWash(accent, alpha);

/** One pastel per story part so the seven page tabs read as a stack of coloured pages. */
export const PART_TINTS = [
  LEARN.color.blue,
  LEARN.color.mint,
  LEARN.color.sun,
  LEARN.color.lavender,
  LEARN.color.coral,
  LEARN.world.ocean.accent,
  LEARN.world.rainbow.accent,
];

// ── hooks ────────────────────────────────────────────────────────────────────

/** Fire onComplete at most once for this mount. */
export function useCompleteOnce(onComplete) {
  const done = useRef(false);
  return useCallback(() => {
    if (done.current) return;
    done.current = true;
    onComplete?.();
  }, [onComplete]);
}

/** Gentle shake on a wrong pick: `shake(id)` then `isShaking(id)` for ~460ms. */
export function useShake(ms = 460) {
  const [id, setId] = useState(null);
  const timer = useRef(null);
  const shake = useCallback(
    (key = "global") => {
      setId(key);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setId(null), ms);
    },
    [ms],
  );
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);
  return { shakeId: id, shake, isShaking: (key = "global") => id === key };
}

/** Counter that re-keys a ConfettiBurst. */
export function useBurst() {
  const [burst, setBurst] = useState(0);
  const fire = useCallback(() => setBurst((b) => b + 1), []);
  return [burst, fire];
}

/** setTimeout that is cleared on unmount. */
export function useLater() {
  const timers = useRef(new Set());
  useEffect(() => {
    const set = timers.current;
    return () => set.forEach((t) => clearTimeout(t));
  }, []);
  return useCallback((fn, ms = 0) => {
    const t = setTimeout(() => {
      timers.current.delete(t);
      fn();
    }, ms);
    timers.current.add(t);
    return t;
  }, []);
}

/**
 * Speaks a round's own cue (the sound, the word to build…) — never the
 * activity prompt, which the player already speaks on mount. The first cue is
 * queued behind that prompt; later rounds interrupt.
 */
export function useRoundCue(speak, text, roundKey = 0, { delay = 380, firstDelay = 900 } = {}) {
  const first = useRef(true);
  useEffect(() => {
    if (!text) return undefined;
    const isFirst = first.current;
    first.current = false;
    const t = setTimeout(
      () => speak?.(text, isFirst ? { dedupe: false } : { interrupt: true, dedupe: false }),
      isFirst ? firstDelay : delay,
    );
    return () => clearTimeout(t);
  }, [text, roundKey, speak, delay, firstDelay]);
}

/** Friendly countdown: returns seconds left + 0..1 progress; calls onDone once. */
export function useTimer(seconds, onDone) {
  const [left, setLeft] = useState(seconds);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    const started = Date.now();
    let fired = false;
    const id = setInterval(() => {
      const remain = Math.max(0, seconds - Math.floor((Date.now() - started) / 1000));
      setLeft(remain);
      if (remain <= 0 && !fired) {
        fired = true;
        clearInterval(id);
        doneRef.current?.();
      }
    }, 250);
    return () => clearInterval(id);
  }, [seconds]);
  return { left, progress: seconds ? left / seconds : 0 };
}

/** Measure an element (for canvases that need pixel sizes). */
export function useMeasure() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const next = { width: Math.max(1, Math.round(el.clientWidth)), height: Math.max(1, Math.round(el.clientHeight)) };
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

/** Mulberry32 shuffle; deterministic per seed. Never returns the identity order for n > 1. */
export function seededShuffle(items, seed = 1) {
  const arr = [...(items || [])];
  let t = (Number(seed) || 1) >>> 0;
  const rand = () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  if (arr.length > 1 && arr.every((v, i) => v === items[i])) {
    arr.push(arr.shift());
  }
  return arr;
}

// ── layout primitives ────────────────────────────────────────────────────────

const KEYFRAMES = `
@keyframes wt-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
@keyframes wt-pop { 0%{transform:scale(1)} 45%{transform:scale(1.14)} 100%{transform:scale(1)} }
@keyframes wt-pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
@keyframes wt-rise { from{opacity:0;transform:translateY(8px) scale(0.96)} to{opacity:1;transform:none} }
.wt-focus:focus-visible { outline: 3px solid ${LEARN.color.blue}; outline-offset: 3px; }
`;

/**
 * Engine root: fills the arena. The working column sits centred (vertically
 * and horizontally) when it is shorter than the arena and scrolls inside the
 * root when it is taller — the page never scrolls. A flex:1 child (a pad)
 * still takes all the spare height.
 */
export function Arena({ children, style, gap = 12, scroll = true, width = 1 }) {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: scroll ? "auto" : "hidden",
        fontFamily: LEARN.font,
        color: INK,
        WebkitOverflowScrolling: "touch",
        ...style,
      }}
    >
      <style>{KEYFRAMES}</style>
      <div
        style={{
          flex: "1 0 auto",
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap,
          width: width >= 1 ? "100%" : `min(100%, calc(var(--arena-w, 760px) * ${width}))`,
          alignSelf: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** A sheet of paper (frosted, faint grain), legible over the light wash. */
export function Panel({ children, style, pad = 14, accent, glow = false }) {
  return (
    <div
      style={{
        ...paperSurface({ frosted: true, border: glow && accent ? accent : LEARN.color.line }),
        padding: pad,
        flexShrink: 0,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * The working surface for a round: paper with an accent header strip naming
 * the activity, sized to its content plus generous padding (never stretched
 * to fill the arena), pieces centred inside.
 */
export function Board({
  children,
  title,
  symbol,
  accent = LEARN.color.blue,
  right,
  caption,
  style,
  bodyStyle,
  minHeight = "clamp(160px, calc(var(--arena-h, 600px) * 0.3), 300px)",
  pad = "calc(var(--tile, 72px) * 0.28) calc(var(--tile, 72px) * 0.26)",
}) {
  return (
    <div style={{ ...paperSurface({ frosted: true }), display: "flex", flexDirection: "column", flexShrink: 0, minWidth: 0, overflow: "hidden", ...style }}>
      {(title || symbol || right) && <PanelHeader symbol={symbol} title={title} accent={accent} right={right} />}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, minHeight, padding: pad, ...bodyStyle }}>
        {caption && <Caption>{caption}</Caption>}
        {children}
      </div>
    </div>
  );
}

/** Wrapper that gives a pad room to breathe (pads are height:100%). */
export function PadDock({ children, style }) {
  return (
    <div style={{ flex: 1, minHeight: 250, display: "flex", flexDirection: "column", ...style }}>
      {children}
    </div>
  );
}

/** Wrapping, centred row of pieces. */
export function Rail({ children, style, gap = 10 }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap, alignItems: "center", justifyContent: "center", ...style }}>
      {children}
    </div>
  );
}

/** "3 of 10" — the shared frosted counter pill (`done` is the current 1-based round). */
export function RoundPill({ done, total, accent = LEARN.color.blue, label, style }) {
  return <CounterPill current={done} done={Math.max(0, done - 1)} total={total} accent={accent} label={label} style={style} />;
}

/** Short muted caption inside the arena (never the prompt). */
export function Caption({ children, style }) {
  return (
    <div style={{ fontSize: 15, fontWeight: 600, color: LEARN.color.muted, textAlign: "center", lineHeight: 1.3, ...style }}>
      {children}
    </div>
  );
}

// ── icons (inline SVG only) ──────────────────────────────────────────────────

export function SpeakerIcon({ size = 22, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10v4h3l4 4V6L7 10H4z" fill={color} />
      <path d="M15 9.5a3.5 3.5 0 010 5" />
      <path d="M17.5 7a7 7 0 010 10" style={{ opacity: 0.6 }} />
    </svg>
  );
}

export function CheckIcon({ size = 20, color = "#fff", stroke = 3.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export function ArrowIcon({ size = 20, color = "#fff", dir = "right" }) {
  const rot = dir === "left" ? 180 : dir === "down" ? 90 : dir === "up" ? -90 : 0;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: `rotate(${rot}deg)` }}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

// ── interactive pieces ───────────────────────────────────────────────────────

/**
 * Wooden sticker tile tones: white face, 2px line border, a 4px bottom edge
 * in the accent-deep colour. Each tone returns its resting and pressed shadows
 * so a press squashes the tile onto its edge.
 */
function toneStyles(tone, accent) {
  const deep = darken(accent);
  const neutral = tileEdge("rgba(11,26,51,0.12)", 3);
  switch (tone) {
    case "selected": {
      const edge = tileEdge(darken(accent, 0.5), 4);
      return { background: accent, color: "#fff", border: `2px solid ${deep}`, boxShadow: edge.rest, pressed: edge.pressed };
    }
    case "correct": {
      const edge = tileEdge(LEARN.color.mintDeep, 4);
      return { background: LEARN.color.mint, color: "#fff", border: `2px solid ${LEARN.color.mintDeep}`, boxShadow: edge.rest, pressed: edge.pressed };
    }
    case "wrong": {
      const edge = tileEdge(LEARN.color.coralDeep, 4);
      return { background: "#fff", color: INK, border: `2px solid ${LEARN.color.coral}`, boxShadow: edge.rest, pressed: edge.pressed };
    }
    case "hint":
      return { background: wash(accent, 0.14), color: darken(accent, 0.15), border: `2px solid ${wash(accent, 0.5)}`, boxShadow: neutral.rest, pressed: neutral.pressed };
    case "faded": {
      const edge = tileEdge(deep, 4);
      return { background: "#fff", color: INK, border: `2px solid ${LEARN.color.line}`, boxShadow: edge.rest, pressed: edge.pressed, opacity: 0.45 };
    }
    case "blank":
      return { background: "rgba(255,255,255,0.6)", color: "transparent", border: "2.5px dashed rgba(11,26,51,0.2)", boxShadow: "none", pressed: "none" };
    case "quiet":
      return { background: "#fff", color: INK, border: `2px solid ${LEARN.color.line}`, boxShadow: neutral.rest, pressed: neutral.pressed };
    default: {
      const edge = tileEdge(deep, 4);
      return { background: "#fff", color: INK, border: `2px solid ${LEARN.color.line}`, boxShadow: edge.rest, pressed: edge.pressed };
    }
  }
}

const liftProps = (interactive, reduceMotion, pressed) =>
  interactive && !reduceMotion
    ? { whileHover: { y: -3 }, whileTap: { y: 3, scale: 0.98, boxShadow: pressed } }
    : interactive
      ? { whileTap: { scale: 0.98 } }
      : {};

/**
 * Big letter tile. `drag` = the object returned by dragProps(); `as="div"`
 * when it sits inside another button (a filled slot). Glyphs stay in the
 * 26–34px band whatever the tile size; pass `fontScale` for oversized marks.
 */
export function LetterTile({
  label,
  accent = LEARN.color.blue,
  tone = "idle",
  size = 1,
  onClick,
  drag,
  ariaLabel,
  reduceMotion,
  shaking,
  pop,
  pulse,
  disabled,
  as = "button",
  style,
  children,
  fontScale,
}) {
  const Comp = as === "div" ? motion.div : motion.button;
  const interactive = !disabled && !!(onClick || drag);
  const { pressed, ...tones } = toneStyles(tone, accent);
  return (
    <Comp
      type={as === "div" ? undefined : "button"}
      className="wt-focus"
      aria-label={as === "div" ? undefined : ariaLabel || String(label ?? "")}
      disabled={as === "div" ? undefined : disabled}
      onClick={disabled ? undefined : onClick}
      {...(disabled ? {} : drag || {})}
      {...liftProps(interactive, reduceMotion, pressed)}
      style={{
        width: `calc(var(--tile) * ${size})`,
        height: `calc(var(--tile) * ${size})`,
        flexShrink: 0,
        borderRadius: "22%",
        display: "grid",
        placeItems: "center",
        padding: 0,
        fontFamily: LEARN.font,
        fontWeight: 700,
        fontSize: fontScale ? `calc(var(--tile) * ${fontScale * size})` : `clamp(18px, calc(var(--tile) * ${size} * 0.4), 34px)`,
        lineHeight: 1,
        cursor: disabled ? "default" : drag ? "grab" : onClick ? "pointer" : "default",
        WebkitTapHighlightColor: "transparent",
        transition: reduceMotion ? "none" : "background 160ms, box-shadow 160ms, border-color 160ms, opacity 200ms",
        animation:
          !reduceMotion && shaking ? "wt-shake 0.46s ease" : !reduceMotion && pop ? "wt-pop 0.5s ease" : !reduceMotion && pulse ? "wt-pulse 1.6s ease-in-out infinite" : undefined,
        ...tones,
        ...(disabled ? {} : drag?.style || {}),
        ...style,
      }}
    >
      {children ?? label}
    </Comp>
  );
}

/** Square drop slot (letters): dashed outline that fills with an accent wash on hover. */
export const Slot = React.forwardRef(function Slot(
  { accent = LEARN.color.blue, hover, size = 1, children, onClick, ariaLabel, index, filled, shaking, reduceMotion, style, active },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className="wt-focus"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        width: `calc(var(--tile) * ${size})`,
        height: `calc(var(--tile) * ${size})`,
        flexShrink: 0,
        borderRadius: "22%",
        border: filled ? "2px solid transparent" : `2.5px dashed ${hover || active ? accent : "rgba(11,26,51,0.22)"}`,
        background: hover ? wash(accent, 0.22) : filled ? "transparent" : active ? wash(accent, 0.1) : "rgba(255,255,255,0.55)",
        display: "grid",
        placeItems: "center",
        padding: 0,
        position: "relative",
        cursor: "pointer",
        fontFamily: LEARN.font,
        WebkitTapHighlightColor: "transparent",
        transform: hover && !reduceMotion ? "scale(1.06)" : "scale(1)",
        transition: reduceMotion ? "none" : "transform 140ms, background 140ms, border-color 140ms",
        animation: !reduceMotion && shaking ? "wt-shake 0.46s ease" : undefined,
        ...style,
      }}
    >
      {children}
      {index != null && !filled && (
        <span aria-hidden="true" style={{ position: "absolute", bottom: 5, right: 9, fontSize: 12, fontWeight: 700, color: LEARN.color.muted }}>
          {index}
        </span>
      )}
    </button>
  );
});

/** Word card for sentence rails: the same wooden tile, word-shaped. */
export function WordCard({
  text,
  accent = LEARN.color.blue,
  tone = "idle",
  size = "md",
  onClick,
  drag,
  ariaLabel,
  reduceMotion,
  shaking,
  pop,
  disabled,
  as = "button",
  style,
  children,
}) {
  const Comp = as === "div" ? motion.div : motion.button;
  const interactive = !disabled && !!(onClick || drag);
  const big = size === "lg";
  const { pressed, ...tones } = toneStyles(tone, accent);
  return (
    <Comp
      type={as === "div" ? undefined : "button"}
      className="wt-focus"
      aria-label={as === "div" ? undefined : ariaLabel || text}
      disabled={as === "div" ? undefined : disabled}
      onClick={disabled ? undefined : onClick}
      {...(disabled ? {} : drag || {})}
      {...liftProps(interactive, reduceMotion, pressed)}
      style={{
        minHeight: big ? 64 : size === "sm" ? 48 : 56,
        minWidth: tone === "blank" ? "calc(var(--tile) * 1.5)" : big ? 64 : size === "sm" ? 48 : 56,
        padding: big ? "10px 22px" : size === "sm" ? "6px 14px" : "8px 18px",
        borderRadius: 18,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontFamily: LEARN.font,
        fontWeight: 700,
        fontSize: big ? 26 : size === "sm" ? 19 : 22,
        lineHeight: 1.15,
        whiteSpace: "nowrap",
        cursor: disabled ? "default" : drag ? "grab" : onClick ? "pointer" : "default",
        WebkitTapHighlightColor: "transparent",
        transition: reduceMotion ? "none" : "background 160ms, box-shadow 160ms, border-color 160ms, opacity 240ms",
        animation: !reduceMotion && shaking ? "wt-shake 0.46s ease" : !reduceMotion && pop ? "wt-pop 0.5s ease" : undefined,
        ...tones,
        ...(disabled ? {} : drag?.style || {}),
        ...style,
      }}
    >
      {children ?? text}
    </Comp>
  );
}

/** Wide dashed slot in a sentence rail (word-shaped). */
export const WordSlot = React.forwardRef(function WordSlot(
  { accent = LEARN.color.blue, hover, active, children, onClick, ariaLabel, label, shaking, reduceMotion, style, minWidth = "calc(var(--tile) * 1.6)" },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className="wt-focus"
      aria-label={ariaLabel || label}
      onClick={onClick}
      style={{
        minHeight: 56,
        minWidth,
        padding: children ? 0 : "8px 14px",
        borderRadius: 18,
        border: children ? "2px solid transparent" : `2.5px dashed ${hover || active ? accent : "rgba(11,26,51,0.22)"}`,
        background: hover ? wash(accent, 0.22) : children ? "transparent" : active ? wash(accent, 0.1) : "rgba(255,255,255,0.55)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: LEARN.font,
        fontWeight: 700,
        fontSize: 15,
        color: LEARN.color.muted,
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        transform: hover && !reduceMotion ? "scale(1.05)" : "scale(1)",
        transition: reduceMotion ? "none" : "transform 140ms, background 140ms, border-color 140ms",
        animation: !reduceMotion && shaking ? "wt-shake 0.46s ease" : undefined,
        ...style,
      }}
    >
      {children || label || ""}
    </button>
  );
});

/**
 * White sticker frame with a soft shadow around a hosted cutout at ~80%,
 * sized by var(--tile). `cast` uses CastPicture. Selected / correct / wrong
 * tones take the wooden-tile edge so state reads at a glance.
 */
export function PictureTile({
  name,
  alt,
  accent = LEARN.color.blue,
  size = 1.4,
  tone = "idle",
  onClick,
  drag,
  ariaLabel,
  reduceMotion,
  shaking,
  pop,
  badge,
  label,
  cast = false,
  disabled,
  as = "button",
  style,
}) {
  const Comp = as === "div" ? motion.div : motion.button;
  const interactive = !disabled && !!(onClick || drag);
  const Pic = cast ? CastPicture : WordPicture;
  const framed = tone === "idle" || tone === "quiet" || tone === "faded";
  const { pressed: tonePressed, ...tones } = toneStyles(tone, accent);
  const rest = framed ? PICTURE_FRAME.rest : tones.boxShadow;
  const pressed = framed ? PICTURE_FRAME.pressed : tonePressed;
  return (
    <Comp
      type={as === "div" ? undefined : "button"}
      className="wt-focus"
      aria-label={as === "div" ? undefined : ariaLabel || alt || name}
      disabled={as === "div" ? undefined : disabled}
      onClick={disabled ? undefined : onClick}
      {...(disabled ? {} : drag || {})}
      {...liftProps(interactive, reduceMotion, pressed)}
      style={{
        width: `calc(var(--tile) * ${size})`,
        height: label ? "auto" : `calc(var(--tile) * ${size})`,
        flexShrink: 0,
        borderRadius: LEARN.radius.md,
        padding: label ? "8% 6% 7%" : "6%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        position: "relative",
        fontFamily: LEARN.font,
        fontWeight: 700,
        fontSize: 18,
        cursor: disabled ? "default" : drag ? "grab" : onClick ? "pointer" : "default",
        WebkitTapHighlightColor: "transparent",
        transition: reduceMotion ? "none" : "background 160ms, box-shadow 160ms, border-color 160ms, opacity 200ms",
        animation: !reduceMotion && shaking ? "wt-shake 0.46s ease" : !reduceMotion && pop ? "wt-pop 0.5s ease" : undefined,
        ...tones,
        ...(framed ? { border: "2px solid rgba(11,26,51,0.08)" } : null),
        boxShadow: rest,
        ...(disabled ? {} : drag?.style || {}),
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: label ? `calc(var(--tile) * ${size} * 0.72)` : "100%",
          height: label ? `calc(var(--tile) * ${size} * 0.72)` : "100%",
          display: "grid",
          placeItems: "center",
          pointerEvents: "none",
          filter: "drop-shadow(0 4px 6px rgba(11,26,51,0.14))",
        }}
      >
        <Pic name={name} alt={alt || name} plain size="100%" />
      </span>
      {label && <span style={{ lineHeight: 1.1, textAlign: "center", pointerEvents: "none" }}>{label}</span>}
      {badge != null && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -8,
            right: -8,
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: accent,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: 15,
            fontWeight: 700,
            boxShadow: LEARN.shadow.sticker(darken(accent)),
          }}
        >
          {badge}
        </span>
      )}
    </Comp>
  );
}

/** Round speaker button. */
export function SpeakerButton({ onClick, accent = LEARN.color.blue, size = 48, ariaLabel = "Hear it", reduceMotion, pulse, style }) {
  return (
    <motion.button
      type="button"
      className="wt-focus"
      aria-label={ariaLabel}
      onClick={onClick}
      whileTap={reduceMotion ? undefined : { scale: 0.92 }}
      whileHover={reduceMotion ? undefined : { y: -2 }}
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        background: accent,
        boxShadow: LEARN.shadow.sticker(darken(accent)),
        display: "grid",
        placeItems: "center",
        WebkitTapHighlightColor: "transparent",
        animation: pulse && !reduceMotion ? "wt-pulse 1.6s ease-in-out infinite" : undefined,
        ...style,
      }}
    >
      <SpeakerIcon size={Math.round(size * 0.46)} />
    </motion.button>
  );
}

/** Chunky sticker action (a verb, never "Next"). */
export function ActionButton({ children, accent = LEARN.color.blue, onClick, disabled, size = "md", reduceMotion, ariaLabel, style, icon }) {
  return (
    <motion.button
      type="button"
      className="wt-focus"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      whileTap={!disabled && !reduceMotion ? { scale: 0.96, y: 2 } : undefined}
      whileHover={!disabled && !reduceMotion ? { y: -2 } : undefined}
      style={{
        ...stickerButton({ bg: disabled ? "rgba(11,26,51,0.22)" : accent, deep: disabled ? "rgba(11,26,51,0.12)" : darken(accent), size }),
        minHeight: size === "lg" ? 58 : size === "sm" ? 42 : 50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {icon}
      {children}
    </motion.button>
  );
}

/** Check-box card (checklists): a real button with a check circle. */
export function CheckCard({ text, checked, accent = LEARN.color.blue, onClick, reduceMotion, style }) {
  return (
    <motion.button
      type="button"
      className="wt-focus"
      role="checkbox"
      aria-checked={!!checked}
      onClick={onClick}
      whileTap={reduceMotion ? undefined : { scale: 0.97 }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        textAlign: "left",
        minHeight: 60,
        padding: "10px 14px",
        borderRadius: 18,
        border: `2px solid ${checked ? accent : LEARN.color.line}`,
        background: checked ? wash(accent, 0.12) : "#fff",
        boxShadow: tileEdge(checked ? darken(accent) : "rgba(11,26,51,0.12)", 3).rest,
        fontFamily: LEARN.font,
        fontWeight: 700,
        fontSize: 20,
        color: INK,
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        transition: reduceMotion ? "none" : "background 160ms, border-color 160ms",
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "50%",
          background: checked ? accent : "rgba(11,26,51,0.08)",
          display: "grid",
          placeItems: "center",
          transition: reduceMotion ? "none" : "background 160ms",
        }}
      >
        {checked && <CheckIcon size={18} />}
      </span>
      <span style={{ lineHeight: 1.25 }}>{text}</span>
    </motion.button>
  );
}

/** Friendly SVG progress ring (timers, meters) — no scary countdown numbers. */
export function ProgressRing({ progress = 1, accent = LEARN.color.blue, size = 80, label, sub, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, progress));
  return (
    <div role="img" aria-label={sub ? `${label} ${sub}` : String(label ?? "")} style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="rgba(255,255,255,0.9)" stroke="rgba(11,26,51,0.08)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - p)}
          style={{ transition: "stroke-dashoffset 400ms linear" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
        <span style={{ fontSize: size * 0.34, fontWeight: 700, color: INK }}>{label}</span>
        {sub && <span style={{ fontSize: Math.max(11, size * 0.14), fontWeight: 700, color: LEARN.color.muted, marginTop: 2 }}>{sub}</span>}
      </div>
    </div>
  );
}

// ── storybook ────────────────────────────────────────────────────────────────

/**
 * Row of coloured page tabs — one pastel per story part, the current page
 * raised above the others. `tabRef(part)` lets tabs be drop targets.
 */
export function PageTabs({ parts, activeId, doneIds = [], accent = LEARN.color.blue, onSelect, tabRef, hoverId, compact, reduceMotion, shakingId }) {
  return (
    <div role="tablist" aria-label="Story parts" style={{ display: "flex", gap: compact ? 4 : 6, width: "100%", alignItems: "flex-end", paddingTop: 4 }}>
      {parts.map((part, i) => {
        const tint = PART_TINTS[i % PART_TINTS.length];
        const active = part.id === activeId;
        const done = doneIds.includes(part.id);
        const hover = hoverId === `tab-${part.id}`;
        const lifted = active || hover;
        return (
          <button
            key={part.id}
            ref={tabRef ? tabRef(part) : undefined}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={`${part.title}${done ? ", done" : ""}`}
            className="wt-focus"
            onClick={onSelect ? () => onSelect(part) : undefined}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: compact ? 46 : 56,
              padding: compact ? "6px 3px" : "8px 6px",
              borderRadius: "14px 14px 8px 8px",
              border: `2px solid ${hover ? darken(tint, 0.2) : active ? darken(tint, 0.25) : wash(tint, done ? 0.7 : 0.45)}`,
              background: hover ? wash(tint, 0.42) : active ? tint : wash(tint, done ? 0.34 : 0.2),
              color: active ? "#fff" : darken(tint, 0.38),
              boxShadow: lifted ? LEARN.shadow.sticker(darken(tint, 0.35)) : "none",
              fontFamily: LEARN.font,
              fontWeight: 700,
              fontSize: compact ? 11 : 13,
              lineHeight: 1.1,
              textAlign: "center",
              cursor: onSelect || tabRef ? "pointer" : "default",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              overflow: "hidden",
              WebkitTapHighlightColor: "transparent",
              transform: lifted ? "translateY(-4px)" : "translateY(0)",
              transition: reduceMotion ? "none" : "background 160ms, transform 160ms, border-color 160ms, box-shadow 160ms",
              animation: !reduceMotion && shakingId === part.id ? "wt-shake 0.46s ease" : undefined,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: compact ? 10 : 11, opacity: 0.8 }}>{i + 1}</span>
            <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{part.title}</span>
            {done && !active && (
              <span aria-hidden="true" style={{ display: "inline-grid", placeItems: "center", width: 14, height: 14, borderRadius: "50%", background: darken(tint, 0.1) }}>
                <CheckIcon size={9} stroke={4} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Storybook: coloured page tabs across the top of a sheet of paper, optional
 * page art at the side, large readable text. Sized to its content unless
 * `fill`; wrap the writing pad OUTSIDE (below) it.
 */
export function StoryBook({ parts, activeId, doneIds, accent = LEARN.color.blue, art, cast, artAlt, children, compact, tabs = {}, style, bodyStyle, fill = false, minHeight, reduceMotion }) {
  return (
    <Panel style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 0, ...(fill ? { flex: 1 } : {}), ...style }} pad={compact ? 10 : 16}>
      {parts && <PageTabs parts={parts} activeId={activeId} doneIds={doneIds} accent={accent} compact={compact} reduceMotion={reduceMotion} {...tabs} />}
      <div
        style={{
          display: "flex",
          flexDirection: compact ? "column" : "row",
          gap: compact ? 8 : 16,
          alignItems: compact ? "center" : "flex-start",
          minHeight: fill ? 0 : minHeight,
          flex: fill ? 1 : undefined,
          overflow: fill ? "auto" : "visible",
          padding: compact ? "2px 0" : "4px 4px 2px",
          ...bodyStyle,
        }}
      >
        {art && (
          <div style={{ flexShrink: 0, alignSelf: compact ? "center" : "flex-start" }}>
            <PictureTile name={art} alt={artAlt} cast={cast} accent={accent} size={compact ? 1.3 : 1.7} tone="quiet" as="div" />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>{children}</div>
      </div>
    </Panel>
  );
}

/** A big readable story sentence (20–22px on paper); a button when onClick is given. */
export function StoryLine({ text, accent = LEARN.color.blue, tone = "idle", onClick, drag, ariaLabel, badge, reduceMotion, shaking, pop, size = "md", style, as }) {
  const isButton = as !== "div" && (onClick || drag);
  const Comp = isButton ? motion.button : motion.div;
  const active = tone === "selected" || tone === "correct";
  const { pressed, ...base } = toneStyles(tone === "selected" ? "hint" : tone === "correct" ? "correct" : tone === "wrong" ? "wrong" : "quiet", accent);
  return (
    <Comp
      type={isButton ? "button" : undefined}
      className="wt-focus"
      aria-label={isButton ? ariaLabel || text : undefined}
      onClick={onClick}
      {...(drag || {})}
      {...liftProps(!!isButton, reduceMotion, pressed)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        textAlign: "left",
        padding: size === "lg" ? "14px 18px" : "11px 14px",
        borderRadius: 18,
        fontFamily: LEARN.font,
        fontWeight: 600,
        fontSize: size === "lg" ? 22 : 20,
        lineHeight: 1.35,
        cursor: isButton ? (drag ? "grab" : "pointer") : "default",
        borderLeft: active ? `6px solid ${tone === "correct" ? LEARN.color.mintDeep : accent}` : undefined,
        WebkitTapHighlightColor: "transparent",
        transition: reduceMotion ? "none" : "background 160ms, border-color 160ms",
        animation: !reduceMotion && shaking ? "wt-shake 0.46s ease" : !reduceMotion && pop ? "wt-pop 0.5s ease" : undefined,
        ...base,
        ...(drag?.style || {}),
        ...style,
      }}
    >
      {badge != null && (
        <span
          aria-hidden="true"
          style={{
            flexShrink: 0,
            minWidth: 30,
            height: 30,
            padding: "0 8px",
            borderRadius: 999,
            background: tone === "correct" ? "rgba(255,255,255,0.3)" : wash(accent, 0.16),
            color: tone === "correct" ? "#fff" : darken(accent, 0.15),
            display: "grid",
            placeItems: "center",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {badge}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, whiteSpace: "normal" }}>{text}</span>
    </Comp>
  );
}

/** Sentence text with one word highlighted in the accent. */
export function HighlightedText({ text, word, accent = LEARN.color.blue, style }) {
  if (!word) return <span style={style}>{text}</span>;
  const idx = text.indexOf(word);
  if (idx < 0) return <span style={style}>{text}</span>;
  return (
    <span style={style}>
      {text.slice(0, idx)}
      <span style={{ color: darken(accent, 0.1), background: wash(accent, 0.16), borderRadius: 8, padding: "0 6px" }}>{word}</span>
      {text.slice(idx + word.length)}
    </span>
  );
}

// ── text helpers ─────────────────────────────────────────────────────────────

/** "I can ___." → [{text:"I"},{text:"can"},{blank:true,suffix:"."}] */
export function stemTokens(stem) {
  return String(stem || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((tok) => {
      const m = tok.match(/^(_{2,})([.,!?]*)$/);
      if (m) return { blank: true, suffix: m[2] || "" };
      return { text: tok };
    });
}

export const splitWords = (sentence) => String(sentence || "").trim().split(/\s+/).filter(Boolean);

export const capitalize = (w) => (w ? w[0].toUpperCase() + w.slice(1) : w);
